import { GenericStatus } from '../../shared/interfaces/generic-status';
import { JobAction } from './job-action';
import { EntityAction } from '../../entity-actions/interfaces/entity-actions';

export enum JobType {
  INCREMENTAL = 'INCREMENTAL',
  HISTORICAL = 'HISTORICAL',
  TRUNCATE_AND_LOAD = 'TRUNCATE_AND_LOAD',
  REFRESHER = 'REFRESHER',
  STREAMING = 'STREAMING'
}

export enum JobMode {
  STANDARD = 'STANDARD',
  LOAD_ONLY = 'LOAD_ONLY'
}

export interface JobPipeline {
  id: number;
  seqId: number;
  name: string;
  source: {
    sourceType: string;
    logoURL: string;
    darkModeLogoURL: string;
    name: string;
  };
  destination: {
    destinationType: string;
    logoURL: string;
    darkModeLogoURL: string;
    name: string;
  };
}

export interface Job {
  mode: JobType;
  jobId: string;
  pipeline?: JobPipeline;
  startedAt: number;
  duration: number;
  events: {
    failed: number;
    ingested: number;
    loaded: number;
    billable: number;
  };
  status: GenericStatus<JobStatus>;
  actions: EntityAction<JobAction>[];
  isDeletedIntegration: boolean;
  isSkipped: boolean;
  latency: JobLatency;
  isLoadOnly: boolean;
}

export interface JobLatency {
  minLatency?: number;
  meanLatency?: number;
  maxLatency?: number;
}
export interface JobListWithCount {
  total: number;
  data: Job[];
}

export enum JobStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  COMPLETED_WITH_FAILURES = 'COMPLETED_WITH_FAILURES',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  CANCELLING = 'CANCELLING',
  SKIPPED = 'SKIPPED'
}

export enum JobObjectStageStatus {
  NOT_INITIALISED = 'NOT_INITIALISED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  COMPLETED_WITH_FAILURES = 'COMPLETED_WITH_FAILURES',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
}

export enum JobObjectStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  COMPLETED_WITH_FAILURES = 'COMPLETED_WITH_FAILURES',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
}

export interface JobDetails {
  jobId: string;
  mode: JobType;
  status: GenericStatus<JobStatus>;
  stats: JobStats;
  actions: EntityAction<JobAction>[];
  isLoadOnly: boolean;
}

export interface JobStats {
  startedAt: number;
  duration: number;
  objectsSuccess: number;
  objectsQueued: number;
  objectsFailed: number;
  eventsIngested: number;
  eventsLoaded: number;
  eventsFailed: number;
  billableEvents: number;
  nonBillableEvents: number;
  latency: JobLatency;
}

export interface JobObject {
  objectName: string;
  objectId: string;
  startedAt: number;
  duration: number;
  eventsIngested: number;
  eventsLoaded: number;
  eventsFailed: number;
  status: GenericStatus<JobObjectStatus>;
  objectStages: Array<JobObjectStage>;
  latency: JobLatency;
  offset: string;
}

export interface JobObjectListWithCount {
  total: number;
  data: JobObject[];
}

export interface JobObjectStage {
  stage: 'ingest' | 'load';
  startedAt: number;
  duration: number;
  inputEvents: number;
  failedEvents: number;
  outputEvents: number;
  hasFailures: boolean;
  status: GenericStatus<JobObjectStageStatus>;
  updatedAt?: number;
  error?: {
    message: string;
  };
}

export enum FailureHandlingAction {
  DOWNLOAD_FILE = 'DOWNLOAD_FAILURE_FILE'
}

export interface JobObjectFailure {
  id: string;
  failedTs: number;
  count: number;
  reason: string;
  actions?: FailureHandlingAction[];
}

export interface JobObjectFailuresWithCount {
  total: number;
  data: JobObjectFailure[];
}

export enum JobObjectFailureStage {
  INGESTOR = 'INGESTOR',
  LOADER = 'LOADER'
}

export interface JobFailure {
  id: string;
  reason: string;
}

export enum JobSessionLogStatus {
  COMPLETED = 'COMPLETED',
  REQUESTED = 'REQUESTED',
  FAILED = 'FAILED'
}

export interface JobSessionLogTaskResponse {
  status: JobSessionLogStatus;
  preSignedURL?: string;
  message?: string;
}
